import { Link, useParams } from "react-router-dom"
import { phoneNumberAutoFormat } from "../../utils/helper"
import { useContext, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { userService } from "../../services/userService"
import ProfileImage from "../../components/images/ProfileImage"
import { FaRegEye } from "react-icons/fa"
import { FaRegEyeSlash } from "react-icons/fa6"
import DataContext from "../../context/dataProvider"
import { useRoles, useSiteTerminology } from "../../contexts/PublicSiteContext"

export default function UserProfile() {
  const [showPin, setShowPin] = useState<boolean>(false)
  const roles = useRoles()
  const { managementCompanySingular, areasLabel } = useSiteTerminology()
  // get current user logged in user
  const { currentUser } = useContext(DataContext)
  const { id } = useParams()

  const { data: user } = useQuery(["user", id], () => id ? userService.getOne(Number(id)) : Promise.resolve(null), {
    enabled: id !== undefined,
  })

  console.log("User data:", user)
  console.log("User managementCompany:", user?.managementCompany)

  return (
    <div className="w-full min-h-full flex justify-center">
      <section className="p-5 w-full h-full lg:max-w-4xl md:p-5 md:shadow-xl md:rounded-xl bg-white max-h-screen overflow-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">{`${user?.firstName} ${user?.lastName}`}</h1>
          <p className="text-gray-600 mt-2">User Profile</p>
        </div>

        <div className="lg:flex lg:justify-evenly items-start gap-8">
          {/* Left Column - Profile Image and Basic Info */}
          <section className="lg:w-1/3">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="relative w-fit m-auto mb-4">
                <ProfileImage user={user} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">{`${user?.firstName} ${user?.lastName}`}</h2>
              <p className="text-gray-600 mb-1">ID: {user?.id}</p>
              <p className="text-gray-600 mb-1">Role: {user?.roleName || (user?.roleId && roles[user?.roleId])}</p>
              <p className="text-gray-600 mb-3">Start Date: {user?.createdAt && new Date(user.createdAt).toLocaleDateString()}</p>
            </div>
          </section>

          {/* Right Column - Contact Info and Details */}
          <section className="lg:w-2/3">
            <div className="bg-gray-50 rounded-lg p-6">
              {/* Update Button */}
              {((currentUser.roleId && user?.roleId && currentUser.roleId > user.roleId) || (currentUser.id === user?.id)) && (
                <div className="flex justify-end mb-4">
                  <Link to={`../users/update/${user?.id}`} state={{ user }}>
                    <button className="bg-primary text-white p-2 rounded-lg hover:shadow-right-lg transform active:translate-y-1 active:scale-[.99] active:shadow-none font-medium">
                      Update Profile
                    </button>
                  </Link>
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-4">
                {user?.phone && (
                  <div className="flex items-center">
                    <strong className="w-32 text-gray-700">Phone: </strong>
                    <a href={`tel:${user.phone}`} className="text-blue-600 font-medium hover:underline">
                      {phoneNumberAutoFormat(user.phone)}
                    </a>
                  </div>
                )}

                {user?.email && (
                  <div className="flex items-center">
                    <strong className="w-32 text-gray-700">Email: </strong>
                    <a href={`mailto:${user.email}`} className="text-blue-600 font-medium hover:underline">
                      {user.email}
                    </a>
                  </div>
                )}

                {/* Management / Security Company */}
                {user?.managementCompany && (
                  <div className="flex items-center">
                    <strong className="w-32 text-gray-700">{managementCompanySingular}: </strong>
                    <span className="text-gray-800 font-medium">{user.managementCompany.name}</span>
                  </div>
                )}

                {/* PIN */}
                <div className="flex items-center">
                  <strong className="w-32 text-gray-700">PIN: </strong>
                  <div className="flex items-center">
                    {showPin ? (
                      <>
                        <span className="mr-2 font-mono text-gray-800">{user?.pin || "Not set"}</span>
                        <FaRegEye onClick={() => setShowPin(false)} className="text-gray-600 cursor-pointer hover:text-gray-800" />
                      </>
                    ) : (
                      <>
                        <span className="mr-2 font-mono text-gray-800">******</span>
                        <FaRegEyeSlash onClick={() => setShowPin(true)} className="text-gray-600 cursor-pointer hover:text-gray-800" />
                      </>
                    )}
                  </div>
                </div>

                {/* Locations */}
                {user?.userToLocation?.length > 0 && (
                  <div>
                    <strong className="block w-32 text-gray-700 mb-2">Locations: </strong>
                    <ul className="list-disc pl-6 space-y-1">
                      {user.userToLocation.map((uTl: { locationId: any, location: { name: string } }) => (
                        <li key={uTl.locationId} className={`${uTl.locationId === user.defaultLocationId ? "font-semibold text-blue-600" : "text-gray-700"}`}>
                          {uTl.location.name}
                          {uTl.locationId === user.defaultLocationId && " (Default)"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Areas */}
                {user?.userToOrganization?.length > 0 && (
                  <div>
                    <strong className="block w-32 text-gray-700 mb-2">{areasLabel}: </strong>
                    <ul className="list-disc pl-6 space-y-1">
                      {user.userToOrganization.map((uTo: { organizationId: any, organization: { name: string } }) => (
                        <li key={uTo.organizationId} className="text-gray-700">
                          {uTo.organization.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  )
}
